import React, { Component } from 'react';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { connect } from 'react-redux';
import Authentication from '../../session';
import withRouter from '../../lib/withRouter';
import Service from '../../../services';
import { baseUrlAdmin } from '../../../store/apiConstants';
import { getBoardsData, getBranchesLatest } from '../../../store/actions';
import { userAssignedBoards } from '../../../store/selectors/userAssignedBoards';
import ClassetDropdown from '../../../classetComponents/classetDropDown';
import ClassetMultiSelect from '../../../classetComponents/classetMultiSelect';
import LoadingComponent from '../../loadingComponent';
import * as XLSX from 'xlsx';

class TotalReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            reportData: [],

            // Dropdown options
            boardId: null,
            classId: null,
            branchId: null,
            sectionIds: [],
            examinationId: null,

            // Dropdown data
            classes: [],
            branches: [],
            sections: [],
            examinations: [],

            // Report display
            showReport: false,
            subjects: []
        };
        this.service = new Service();
    }

    componentDidMount() {
        this.props.getBoardsData(true);
        this.props.getBranchesLatest(true);

        // Load branches if already available
        if (this.props.branchData?.length) {
            this.loadBranches();
        }
    }

    componentDidUpdate(prevProps) {
        // Load branches when branch data is available
        if (!prevProps.branchData?.length && this.props.branchData?.length) {
            this.loadBranches();
        }
    }

    loadBranches = () => {
        // Filter only level 1 branches (actual branches, not sections)
        const branches = this.props.branchData
            ?.filter(branch => branch.level === 1)
            ?.map(branch => ({
                label: branch.name,
                value: branch.key
            })) || [];
        this.setState({ branches });
    }

    onChangeBoard = (boardId) => {
        this.setState({
            boardId,
            classId: null,
            branchId: null,
            sectionIds: [],
            examinationId: null,
            classes: [],
            sections: [],
            examinations: [],
            subjects: [],
            showReport: false,
            reportData: []
        });

        // Load classes for selected board
        const selectedBoard = this.props.boards?.find(board => board.boardId === boardId);
        if (selectedBoard?.classes?.length) {
            const classes = selectedBoard.classes.map(cls => ({
                label: cls.className,
                value: cls.classId,
                groupId: cls.groupId
            }));
            this.setState({ classes });
        }
    }

    onChangeClass = (classId) => {
        this.setState({
            classId,
            branchId: null,
            sectionIds: [],
            examinationId: null,
            sections: [],
            examinations: [],
            subjects: [],
            showReport: false,
            reportData: []
        });
    }

    onChangeBranch = (branchId) => {
        this.setState({
            branchId,
            sectionIds: [],
            examinationId: null,
            sections: [],
            examinations: [],
            subjects: [],
            showReport: false,
            reportData: []
        }, () => {
            this.loadSections();
            this.loadExaminations();
        });
    }

    loadSections = async () => {
        const { boardId, classId, branchId } = this.state;
        if (!boardId || !classId || !branchId) return;

        try {
            this.setState({ isLoading: true });

            // Find the selected board and grade from boards data
            const selectedBoard = this.props.boards?.find(board => board.boardId === boardId);
            const selectedGrade = selectedBoard?.classes?.find(cls => cls.classId === classId);

            if (selectedGrade?.sections) {
                // Get sections for the selected branch from the grade's sections array
                const sections = selectedGrade.sections
                    .filter(section => section.branchId === branchId)
                    .map(section => ({
                        label: section.sectionName,
                        value: section.sectionId
                    }));

                this.setState({ sections });
            } else {
                this.setState({ sections: [] });
            }
        } catch (error) {
            console.error('Error loading sections:', error);
            this.toast.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load sections',
                life: 3000
            });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    loadExaminations = async () => {
        const { boardId, classId, branchId } = this.state;
        if (!boardId || !classId || !branchId) return;

        try {
            this.setState({ isLoading: true });
            const orgId = localStorage.getItem('orgId');
            const academicYear = localStorage.getItem('userAcademicYear');

            // Build query params - backend will filter examinations
            const queryParams = new URLSearchParams({
                orgId,
                academicYearId: academicYear,
                boardId,
                classId,
                branchId
            });

            const response = await this.service.get(
                `${baseUrlAdmin}/subjective-examinations?${queryParams.toString()}`,
                true
            );

            if (response?.res?.data) {
                const examinations = response.res.data.map(exam => ({
                    label: exam.examName,
                    value: exam.examId,
                    subjects: exam.subjects
                }));

                this.setState({ examinations });
            }
        } catch (error) {
            console.error('Error loading examinations:', error);
            this.toast.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load examinations',
                life: 3000
            });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    onChangeExamination = (examinationId) => {
        const selectedExam = this.state.examinations.find(e => e.value === examinationId);
        this.setState({
            examinationId,
            subjects: selectedExam?.subjects || [],
            showReport: false,
            reportData: []
        });
    }

    validateFilters = () => {
        const { boardId, classId, branchId, sectionIds, examinationId } = this.state;

        if (!boardId) {
            this.toast.show({ severity: 'warn', summary: 'Validation', detail: 'Please select Board', life: 3000 });
            return false;
        }
        if (!classId) {
            this.toast.show({ severity: 'warn', summary: 'Validation', detail: 'Please select Grade', life: 3000 });
            return false;
        }
        if (!branchId) {
            this.toast.show({ severity: 'warn', summary: 'Validation', detail: 'Please select Branch', life: 3000 });
            return false;
        }
        if (!sectionIds?.length) {
            this.toast.show({ severity: 'warn', summary: 'Validation', detail: 'Please select at least one Section', life: 3000 });
            return false;
        }
        if (!examinationId) {
            this.toast.show({ severity: 'warn', summary: 'Validation', detail: 'Please select Examination', life: 3000 });
            return false;
        }
        return true;
    }

    showReport = async () => {
        if (!this.validateFilters()) return;

        try {
            this.setState({ isLoading: true });
            const { examinationId, sectionIds } = this.state;

            const response = await this.service.post(
                `${baseUrlAdmin}/subjective-examinations/results/total-report`,
                {
                    examId: examinationId,
                    sectionIds
                },
                true
            );

            if (response?.res?.data) {
                this.setState({
                    reportData: response.res.data,
                    showReport: true
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            this.toast.show({
                severity: 'error',
                summary: 'Error',
                detail: error?.response?.data?.message || 'Failed to load report data',
                life: 3000
            });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    downloadReport = () => {
        if (!this.validateFilters()) return;

        const { reportData, subjects } = this.state;

        if (!reportData?.length) {
            this.toast.show({
                severity: 'warn',
                summary: 'No Data',
                detail: 'Please show the report first before downloading',
                life: 3000
            });
            return;
        }

        // Prepare data for Excel
        const excelData = reportData.map((student, index) => {
            const row = {
                'S.No': index + 1,
                'Enrollment ID': student.enrollmentId,
                'Student Name': student.studentName,
                'Section Name': student.sectionName
            };

            // Add subject-wise data
            subjects.forEach(subject => {
                const subjectResult = student.subjectResults?.find(sr => sr.subjectId === subject.subjectId);

                // If subject doesn't exist in results at all, or if student is not assigned
                if (!subjectResult || subjectResult.isAssigned === false) {
                    row[`${subject.subjectName} Marks (/${subject.maxMarks})`] = 'N/A';
                    row[`${subject.subjectName} Grade`] = 'N/A';
                    row[`${subject.subjectName} Rank`] = 'N/A';
                    return; // Skip to next subject
                }

                const marks = subjectResult.marksObtained;

                // For Excel: Show 'Absent' if AB, empty string if null, otherwise show the actual value (including 0)
                if (marks === 'AB' || marks === 'ab') {
                    row[`${subject.subjectName} Marks (/${subject.maxMarks})`] = 'Absent';
                } else if (marks === null || marks === undefined) {
                    row[`${subject.subjectName} Marks (/${subject.maxMarks})`] = '';
                } else {
                    row[`${subject.subjectName} Marks (/${subject.maxMarks})`] = marks;
                }

                // Handle grade
                row[`${subject.subjectName} Grade`] = subjectResult.grade === 'AB' ? 'Absent' : (subjectResult.grade || '-');

                // Handle rank
                row[`${subject.subjectName} Rank`] = subjectResult.rank || '-';
            });

            // Add overall data
            row['Total Marks'] = `${student.totalMarksObtained || 0}/${student.totalMaxMarks || 0}`;
            row['Percentage'] = student.percentage?.toFixed(2);
            row['Grade'] = student.overallGrade;
            row['Result'] = student.result || '-';
            row['Total Rank'] = student.overallRank;

            return row;
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Total Report');

        // Download
        const examName = this.state.examinations.find(e => e.value === this.state.examinationId)?.label || 'Exam';
        XLSX.writeFile(wb, `${examName}_Total_Report.xlsx`);

        this.toast.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Report downloaded successfully',
            life: 3000
        });
    }

    renderSubjectColumns = () => {
        const { subjects } = this.state;
        const columns = [];

        subjects.forEach(subject => {
            columns.push(
                <Column
                    key={`${subject.subjectId}-marks`}
                    field={`subjectResults.${subject.subjectId}.marksObtained`}
                    header={`${subject.subjectName} Marks (/${subject.maxMarks})`}
                    body={(rowData) => {
                        const result = rowData.subjectResults?.find(sr => sr.subjectId === subject.subjectId);

                        // If subject doesn't exist in results at all (student not assigned to this subject)
                        if (!result || result.isAssigned === false) {
                            return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>N/A</span>;
                        }

                        const marks = result.marksObtained;

                        if (marks === 'AB' || marks === 'ab') {
                            return <span style={{ color: 'red', fontWeight: 'bold' }}>Absent</span>;
                        }
                        // Show empty cell if marks is null/undefined, otherwise show the actual value (including 0)
                        return marks === null || marks === undefined ? '' : marks;
                    }}
                    style={{ minWidth: '150px' }}
                />
            );
            columns.push(
                <Column
                    key={`${subject.subjectId}-grade`}
                    field={`subjectResults.${subject.subjectId}.grade`}
                    header={`${subject.subjectName} Grade`}
                    body={(rowData) => {
                        const result = rowData.subjectResults?.find(sr => sr.subjectId === subject.subjectId);

                        // If subject doesn't exist in results at all (student not assigned to this subject)
                        if (!result || result.isAssigned === false) {
                            return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>N/A</span>;
                        }

                        const grade = result.grade;

                        if (grade === 'AB' || grade === 'ab') {
                            return <span style={{ color: 'red', fontWeight: 'bold' }}>Absent</span>;
                        }
                        return grade || '-';
                    }}
                    style={{ minWidth: '120px' }}
                />
            );
            columns.push(
                <Column
                    key={`${subject.subjectId}-rank`}
                    field={`subjectResults.${subject.subjectId}.rank`}
                    header={`${subject.subjectName} Rank`}
                    body={(rowData) => {
                        const result = rowData.subjectResults?.find(sr => sr.subjectId === subject.subjectId);

                        // If subject doesn't exist in results at all (student not assigned to this subject)
                        if (!result || result.isAssigned === false) {
                            return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>N/A</span>;
                        }

                        const rank = result.rank;
                        const grade = result.grade;

                        // If student is absent, show '-' instead of rank
                        if (grade === 'AB' || grade === 'ab') {
                            return '-';
                        }
                        return rank || '-';
                    }}
                    style={{ minWidth: '120px' }}
                />
            );
        });

        return columns;
    }

    render() {
        const { reportData, showReport, classes, branches, sections, examinations } = this.state;

        return (
            <div className="grid">
                <Toast ref={(el) => this.toast = el} />

                <div className="col-12">
                    <Card>
                        {/* Filters Section */}
                        <div className="grid p-fluid">
                            <div className="col-12 md:col-6 lg:col-3">
                                <ClassetDropdown
                                    required={true}
                                    label="Board"
                                    placeholder="Select Board"
                                    options={this.props.boards}
                                    value={this.state.boardId}
                                    onChange={(e) => this.onChangeBoard(e.value)}
                                    optionLabel="boardName"
                                    optionValue="boardId"
                                />
                            </div>

                            <div className="col-12 md:col-6 lg:col-3">
                                <ClassetDropdown
                                    required={true}
                                    label="Grade"
                                    placeholder="Select Grade"
                                    options={classes}
                                    value={this.state.classId}
                                    onChange={(e) => this.onChangeClass(e.value)}
                                    disabled={!this.state.boardId}
                                />
                            </div>

                            <div className="col-12 md:col-6 lg:col-3">
                                <ClassetDropdown
                                    required={true}
                                    label="Branch"
                                    placeholder="Select Branch"
                                    options={branches}
                                    value={this.state.branchId}
                                    onChange={(e) => this.onChangeBranch(e.value)}
                                    disabled={!this.state.classId}
                                />
                            </div>

                            <div className="col-12 md:col-6 lg:col-3">
                                <ClassetMultiSelect
                                    required={true}
                                    label="Section(s)"
                                    placeholder="Select Sections"
                                    options={sections}
                                    value={this.state.sectionIds}
                                    onChange={(e) => this.setState({
                                        sectionIds: e.value,
                                        examinationId: null,
                                        subjects: [],
                                        showReport: false,
                                        reportData: []
                                    })}
                                    disabled={!this.state.branchId}
                                    optionLabel="label"
                                    optionValue="value"
                                />
                            </div>

                            <div className="col-12 md:col-6 lg:col-3">
                                <ClassetDropdown
                                    required={true}
                                    label="Examination"
                                    placeholder="Select Examination"
                                    options={examinations}
                                    value={this.state.examinationId}
                                    onChange={(e) => this.onChangeExamination(e.value)}
                                    disabled={!this.state.branchId}
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-4">
                            <Button
                                label="Show Report"
                                icon="pi pi-eye"
                                onClick={this.showReport}
                                className="p-button-primary"
                            />
                            <Button
                                label="Download Report"
                                icon="pi pi-download"
                                onClick={this.downloadReport}
                                className="p-button-success"
                            />
                        </div>

                        {/* Report Table */}
                        {showReport && (
                            <div className="mt-4">
                                <DataTable
                                    value={reportData}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[10, 25, 50]}
                                    scrollable
                                    scrollHeight="500px"
                                    emptyMessage="No data available"
                                    className="mt-3"
                                >
                                    <Column
                                        header="S.No"
                                        body={(rowData, options) => options.rowIndex + 1}
                                        style={{ minWidth: '70px', maxWidth: '70px' }}
                                        frozen
                                    />
                                    <Column
                                        field="enrollmentId"
                                        header="Enrollment ID"
                                        style={{ minWidth: '150px' }}
                                        frozen
                                    />
                                    <Column
                                        field="studentName"
                                        header="Student Name"
                                        style={{ minWidth: '200px' }}
                                        frozen
                                    />
                                    <Column
                                        field="sectionName"
                                        header="Section Name"
                                        style={{ minWidth: '120px' }}
                                    />

                                    {/* Dynamic Subject Columns */}
                                    {this.renderSubjectColumns()}

                                    {/* Overall Columns */}
                                    <Column
                                        field="totalMarks"
                                        header="Total Marks"
                                        body={(rowData) => `${rowData.totalMarksObtained || 0}/${rowData.totalMaxMarks || 0}`}
                                        style={{ minWidth: '140px' }}
                                    />
                                    <Column
                                        field="percentage"
                                        header="Percentage"
                                        body={(rowData) => rowData.percentage?.toFixed(2)}
                                        style={{ minWidth: '120px' }}
                                    />
                                    <Column
                                        field="overallGrade"
                                        header="Grade"
                                        style={{ minWidth: '100px' }}
                                    />
                                    <Column
                                        field="result"
                                        header="Result"
                                        body={(rowData) => rowData.result || '-'}
                                        style={{ minWidth: '100px' }}
                                    />
                                    <Column
                                        field="overallRank"
                                        header="Total Rank"
                                        style={{ minWidth: '120px' }}
                                    />
                                </DataTable>
                            </div>
                        )}
                    </Card>
                </div>

                {this.state.isLoading && <LoadingComponent />}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.user,
    boards: userAssignedBoards(state, 'activeBoardsWithoutMeluha'),
    branchData: state.branchDataLatest?.data?.data || []
});

const mapDispatchToProps = {
    getBoardsData,
    getBranchesLatest
};

export default connect(mapStateToProps, mapDispatchToProps)(Authentication(withRouter(TotalReport)));
